import { createTheme, type MantineColorsTuple } from '@mantine/core';

/**
 * Оформление перекликается с сайтом коллектива (favoritstarss.ru): белый фон,
 * шрифт Raleway, тёплый серый текст #424242, заголовки в верхнем регистре
 * с разрядкой. Акцент используется точечно: кнопки, активный пункт меню,
 * тонкие линии под заголовками.
 *
 * Акцентов два, переключаются константой ACCENT ниже. Компоненты берут не
 * конкретный оттенок, а роли accentFill и accentText, поэтому смена цвета
 * не требует правок за пределами этого файла.
 */

/** Золотой цвет сайта. Шкала растянута в тёмную сторону: сам #d3ac6f на белом
 *  даёт контраст около 2:1, поэтому им заливают, но не пишут. */
const gold: MantineColorsTuple = [
  '#fbf7f0',
  '#f4ebda',
  '#e9d8ba',
  '#ddc294',
  '#d3ac6f', // цвет сайта: заливка кнопок и декоративные линии
  '#c99a52',
  '#b98940',
  '#9c7134',
  '#7e5b2a', // текстовые акценты: ссылки, подписи (около 6:1 на белом)
  '#5f4520',
];

/** Прежний тёмно-синий акцент. */
const navy: MantineColorsTuple = [
  '#eef3fa',
  '#dae3f1',
  '#b3c5e2',
  '#89a4d2',
  '#6789c5',
  '#5178bd',
  '#456fba',
  '#375ea4',
  '#23477f', // заливка кнопок
  '#1b3a6b', // наведение и текстовые акценты
];

/** Нейтральная шкала вокруг цвета текста сайта. */
const graphite: MantineColorsTuple = [
  '#f7f7f8',
  '#efeff0',
  '#dcdcde',
  '#c3c3c6',
  '#a2a2a6',
  '#84848a',
  '#6b6b71',
  '#565659',
  '#484848',
  '#424242',
];

/** Единственное место переключения оформления: 'gold' или 'navy'. */
export const ACCENT: 'gold' | 'navy' = 'gold';

const shades = {
  gold: { fill: 4, text: 8 },
  navy: { fill: 8, text: 9 },
} as const;

/** Заливка кнопок и декоративные линии. */
export const accentFill = `${ACCENT}.${shades[ACCENT].fill}`;

/** Текст на белом: ссылки и подписи. Всегда контрастнее 4,5:1. */
export const accentText = `${ACCENT}.${shades[ACCENT].text}`;

export const theme = createTheme({
  primaryColor: ACCENT,
  // Надпись на заливке белая (autoContrast намеренно выключен). Для золота
  // это около 2:1; поднять читаемость можно, сдвинув fill на 7 — там 4,4:1.
  primaryShade: { light: shades[ACCENT].fill, dark: shades[ACCENT].fill },
  colors: { gold, navy, graphite },
  black: '#424242',
  white: '#ffffff',
  defaultRadius: 'md',
  fontFamily: "'Raleway Variable', 'Raleway', Arial, sans-serif",
  headings: {
    fontFamily: "'Raleway Variable', 'Raleway', Arial, sans-serif",
    fontWeight: '600',
  },
  components: {
    Button: {
      defaultProps: { radius: 'md' },
      styles: { root: { fontWeight: 600, letterSpacing: '0.01em' } },
    },
    Card: {
      defaultProps: { withBorder: true, radius: 'md' },
    },
    Anchor: {
      styles: { root: { fontWeight: 500 } },
    },
    NavLink: {
      // Активный пункт Mantine красит подписью цвета акцента; на золоте это
      // около 2,9:1. Оставляем текст тёмным, активность показывает фон.
      styles: {
        label: { fontSize: 15, fontWeight: 500, color: 'var(--mantine-color-graphite-9)' },
      },
    },
  },
});
